import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from "@tanstack/react-query";

import type { CreateActivityContactSchemaType } from "@/schemas/contactSchema";
import type { UpdateRecapEmailSchemaType } from "@/schemas/recapEmailSchema";
import {
  createContactForActivity,
  fetchCallOutput,
  updateRecapEmail,
} from "@/services/frontendServices/callOutputServices";
import type { CallOutputDto, ContactDto, RecapEmailEditDto } from "@/types";

import { contactKeys } from "./useContacts";

export const callOutputKeys = {
  all: ["callOutput"] as const,
  detail: (activityId: string) => [...callOutputKeys.all, activityId] as const,
};

const CALL_OUTPUT_POLL_MS = 30_000;
/** A Gong/Fathom recording takes minutes to hours to land; no need to poll as often. */
const AWAITING_RECORDING_POLL_MS = 2 * 60 * 1000;

/**
 * Polls only while something can still change on its own: an analysis the
 * agent is running, will retry or has queued, a recording that can still
 * arrive, or a recap or call summary draft in progress. The server says which
 * (`analysisInFlight`, `analysisPendingReason`), so a call that will never be
 * analyzed stops at the first answer instead of waiting out a timer.
 */
export function callOutputRefetchInterval(
  data: CallOutputDto | undefined
): number | false {
  if (data === undefined) return false;
  const analysisRunning =
    data.analysisInFlight || data.analysisPendingReason === "queued";
  const recapDrafting = data.recapEmail?.status === "drafting";
  const summaryDrafting = data.meeting?.summaryStatus === "drafting";
  if (analysisRunning || recapDrafting || summaryDrafting) {
    return CALL_OUTPUT_POLL_MS;
  }
  return data.analysisPendingReason === "awaiting_recording"
    ? AWAITING_RECORDING_POLL_MS
    : false;
}

export const useCallOutput = (
  activityId: string,
  options?: Partial<UseQueryOptions<CallOutputDto, Error>>
) =>
  useQuery<CallOutputDto, Error>({
    queryKey: callOutputKeys.detail(activityId),
    queryFn: () => fetchCallOutput(activityId),
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: "always",
    refetchInterval: (query) => callOutputRefetchInterval(query.state.data),
    ...options,
  });

export const useCreateContactForActivity = (activityId: string) => {
  const queryClient = useQueryClient();

  return useMutation<ContactDto, Error, CreateActivityContactSchemaType>({
    mutationFn: (data) => createContactForActivity(activityId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: callOutputKeys.detail(activityId),
      });
      queryClient.invalidateQueries({ queryKey: contactKeys.all });
    },
  });
};

export const useUpdateRecapEmail = (activityId: string) => {
  const queryClient = useQueryClient();

  return useMutation<RecapEmailEditDto, Error, UpdateRecapEmailSchemaType>({
    mutationFn: (data) => updateRecapEmail(activityId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: callOutputKeys.detail(activityId),
      });
    },
  });
};
